import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { createTask } from '../../features/schedulerSlice'
import { useDispatch, useSelector } from 'react-redux'
import { Avatar, Box, Button, Grid, IconButton, Stack, Typography } from '@mui/material'

import ButtonCustom from '../../components/atoms/button/ButtonCustom'
import CalendarSync from '../../components/atoms/CalendarSync/CalendarSync'
import ListToolbar from '../../components/dataGrid/ListToolBar'
import FilterPopover from '../../components/dataGrid/FilterPopover'
import DataGridTable from '../../components/dataGrid/DataGridTable'
import StatusBtn from '../../components/atoms/status/StatusBtn'
import KebabMenu from '../../components/dataGrid/KebabMenu'
import CommonModal from '../../components/atoms/modal/CommonModal'
import MultiselectSearch from '../../components/atoms/select/MultiSelect'
import DateCustom from '../../components/atoms/input/DateCustom'
import SelectCustom from '../../components/atoms/select/SelectCustom'
import TextArea from '../../components/atoms/textarea/TextArea'
import FileAttach from '../../components/atoms/file-attach/FileAttach'
import CommonDeleteModal from '../../components/atoms/modal/CommonDeleteModal'
import { toastHelper } from '../../util/toastHelper'
import { getKazooMembers } from '../../features/kazooEventSlice'
import { getKazooGuests } from '../../features/userSlice'
import { GeneralPaymentPopup } from '../../components/popups/GeneralPaymentPopup'
import {
	getEvents,
	deleteTask,
	updateTask,
} from '../../features/schedulerSlice'
import { fetchEventTags } from '../../features/guestListSlice'
import EventTagsField from '../../components/tags/EventTagsField'
import { useTutorial } from '../../context/TutorialContext'
import { useTutorialStatus } from '../../hooks/useTutorialStatus'
import { EVENT_CATEGORIES } from '../../util/constants'
import { getDefaultSyncFromConnections } from '../../util/calendarProviders'
import { formatUtcToLocalDate } from '../../util/formatDateAndTime'
import styles from './KazooTasksPage.module.scss'
import { useCoupleHasPremium } from '../../hooks/index.js'
import { TASK_STATUS_OPTIONS } from '../../util/constants'
import SyncAllModal from './SyncAllModal.jsx'

function esc(s) {
	const str = String(s ?? '')
	return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
}

const availableStatuses = TASK_STATUS_OPTIONS.filter(
	(option) => option.value !== 'Completed'
).map((option) => option.value)

export default function KazooTasksPage() {
	const [search, setSearch] = useState('')
	const [statusFilter, setStatusFilter] = useState(availableStatuses)
	const [priorityFilter, setPriorityFilter] = useState([])
	const [filterAnchor, setFilterAnchor] = useState(null)
	const [menuAnchor, setMenuAnchor] = useState(null)
	const [menuRow, setMenuRow] = useState(null)
	const [selectedMembers, setSelectedMembers] = useState([])
	const [text, setText] = useState('')
	const [activeModal, setActiveModal] = useState('')
	const [taskTitle, setTaskTitle] = useState('')
	const [currentTaskId, setCurrentTaskId] = useState(null)
	const [currentTask, setCurrentTask] = useState(null)
	const [dueDate, setDueDate] = useState(null) // 'yyyy-mm-dd' string or Date
	const [priorityVal, setPriorityVal] = useState('high') // 'high' | 'medium' | 'low'
	const [statusVal, setStatusVal] = useState('new') // 'new' | 'inprogress' | 'completed' | 'overdue'
	const { kazooId } = useParams()
	const [eventCategory, setEventCategory] = useState({
		value: '',
		label: '',
	})
	const [eventTags, setEventTags] = useState([])
	const [syncToCalendar, setSyncToCalendar] = useState({
		google: true,
		outlook: true,
		ical: true,
	})
	// Tutorial hooks
	const { hasSeenTutorial, isLoading } = useTutorialStatus('tasks')
	const { startTutorial, isTutorialActive } = useTutorial()
	// Tutorial effect
	useEffect(() => {
		if (!isLoading && !hasSeenTutorial && !isTutorialActive()) {
			const timer = setTimeout(() => {
				startTutorial('tasks')
			}, 1000)
			return () => clearTimeout(timer)
		}
	}, [isLoading, hasSeenTutorial, isTutorialActive, startTutorial])
	const [showUpgradePopup, setShowUpgradePopup] = useState(false)
	const connections = useSelector((state) => state.calendar.connections)

	const dispatch = useDispatch()

	useEffect(() => {
		if (activeModal === 'create') {
			setSyncToCalendar(getDefaultSyncFromConnections(connections))
		}
	}, [activeModal, connections])

	const eventCategoriesOptions = useMemo(() => 
		EVENT_CATEGORIES.map((category) => ({
			value: category,
			label: category,
		})),
		[EVENT_CATEGORIES]
	)

	const PRIORITY_LABEL = { high: 0, medium: 1, low: 2 }
	const STATUS_LABEL = {
		new: 'New',
		inprogress: 'In Progress',
		completed: 'Completed',
		overdue: 'Overdue',
	}

	const allEvents = useSelector((s) => s.scheduler.timelineEvents) || []
	const eventTagCatalog = useSelector((s) => s.guestList.eventTags) || []

	const uniqueEvents = useMemo(() => {
		const m = new Map()
		for (const e of allEvents) {
			if (!m.has(e._id)) m.set(e._id, e)
		}
		return Array.from(m.values())
	}, [allEvents])

	const tasksOnly = useMemo(
		() => uniqueEvents,
		[uniqueEvents]
	)

	const getVal = (v) => (typeof v === 'string' ? v : v?.value || '')

	const rows = useMemo(() => {
		const priorityMap = { 0: 'High', 1: 'Medium', 2: 'Low' }

		return tasksOnly.map((t) => ({
			id: t._id,
			task: t.Subject || 'Untitled Task',
			assignedTo:
				Array.isArray(t.OwnerIds) && t.OwnerIds.length
					? `${t.OwnerIds.length} assignee${t.OwnerIds.length > 1 ? 's' : ''}`
					: 'Unassigned',
			avatar: undefined,
			dueDate: formatUtcToLocalDate(t.StartTime || t.EndTime),
			priority: priorityMap[Number(t.priority)] || 'Unknown',
			comments: t.Description || '-',
			status: t.status || 'New',
		}))
	}, [tasksOnly])

	const priority_options = [
		{
			value: 'high',
			label: (
				<div className={`${styles.prioritySelect} prioritySelect`}>
					<img src="/images/high-prio-icon.svg" alt="high" />
					High
				</div>
			),
		},
		{
			value: 'medium',
			label: (
				<div className={`${styles.prioritySelect} prioritySelect`}>
					<img src="/images/med-prio-icon.svg" alt="med" />
					Medium
				</div>
			),
		},
		{
			value: 'low',
			label: (
				<div className={`${styles.prioritySelect} prioritySelect`}>
					<img src="/images/low-prio-icon.svg" alt="low" />
					Low
				</div>
			),
		},
	]
	const status_options = [
		{
			value: 'new',
			label: (
				<StatusBtn
					label="New"
					variant="active"
					className={styles.statusOption}
				/>
			),
		},
		{
			value: 'inprogress',
			label: (
				<StatusBtn
					label="In Progress"
					variant="pending"
					className={styles.statusOption}
				/>
			),
		},
		{
			value: 'completed',
			label: (
				<StatusBtn
					label="Completed"
					variant="submitted"
					className={styles.statusOption}
				/>
			),
		},
		{
			value: 'overdue',
			label: (
				<StatusBtn
					label="Overdue"
					variant="closed"
					className={styles.statusOption}
				/>
			),
		},
	]

	const normVal = (v) =>
		typeof v === 'string' ? v : (v?.value ?? v?.target?.value ?? '')

	const handleStatusChange = (v, rowId) => {
		const statusKey = normVal(v)

		if (!rowId) {
			setStatusVal(statusKey)
			return
		}

		const task = tasksOnly.find((t) => String(t._id) === String(rowId))
		if (!task) return

		const statusLabel = STATUS_LABEL[statusKey] || 'New'

		const payload = {
			title: task.Subject || 'Untitled Task',
			selectedMembers: (Array.isArray(task.OwnerIds) ? task.OwnerIds : [])
				.map((id) => peopleById.get(String(id)))
				.filter(Boolean),
			StartTime: task.StartTime,
			EndTime: task.EndTime,
			priority: task.priority ?? 0,
			status: statusLabel,
			comments: task.Description || '',
			eventCategory: task.EventCategory || '',
			eventTags: task.EventTags || [],
		}

		dispatch(updateTask({ kazooId, id: rowId, data: payload }))
	}

	const columns = useMemo(
		() => [
			{
				field: 'task',
				headerName: 'Task',
				flex: 1,
				minWidth: 220,
				sortable: true,
				renderCell: (params) => (
					<span className="role-text">{params.value || '-'}</span>
				),
			},
			{
				field: 'assignedTo',
				headerName: 'Assigned To',
				flex: 1.6,
				minWidth: 160,
				sortable: true,
				renderCell: (params) => {
					const { avatar, assignedTo } = params.row
					return (
						<Stack
							direction="row"
							spacing={1.5}
							alignItems="center"
							sx={{ height: '100%' }}
							className="img-with-text-wrp"
						>
							<Avatar
								src={
									avatar ||
									'https://static.vecteezy.com/system/resources/previews/020/911/740/non_2x/user-profile-icon-profile-avatar-user-icon-male-icon-face-icon-profile-icon-free-png.png'
								}
								sx={{
									width: 40,
									height: 40,
									borderRadius: '50%',
									'@media (max-width: 767px)': {
										width: 30,
										height: 30,
									},
								}}
							/>
							<Box
								component="span"
								sx={{ fontWeight: 600 }}
								className="img-with-text"
							>
								{assignedTo}
							</Box>
						</Stack>
					)
				},
			},
			{
				field: 'dueDate',
				headerName: 'Due Date',
				flex: 1,
				minWidth: 140,
				sortable: true,
				renderCell: (params) => (
					<span className="role-text">{params.value || '-'}</span>
				),
			},
			{
				field: 'priority',
				headerName: 'Priority',
				flex: 1,
				minWidth: 100,
				renderCell: (params) => {
					const { priority } = params.row
					return (
						<Stack
							direction="row"
							spacing={1.5}
							alignItems="center"
						>
							<Box
								component="span"
								sx={{ fontWeight: 600 }}
								className="priority-icon"
							>
								<img
									src={
										priority === 'High'
											? '/images/high-prio-icon.svg'
											: priority === 'Medium'
												? '/images/med-prio-icon.svg'
												: '/images/low-prio-icon.svg'
									}
									alt="priority.png"
								/>
								{priority}
							</Box>
						</Stack>
					)
				},
			},
			{
				field: 'comments',
				headerName: 'Comments',
				flex: 1,
				minWidth: 200,
				sortable: false,
				renderCell: (params) => (
					<span className="role-text">{params.value || '-'}</span>
				),
			},
			{
				field: 'status',
				headerName: 'Status',
				flex: 1,
				minWidth: 130,
				renderCell: (params) => {
					const status = params.value
					const rowId = params.row.id

					return (
						<SelectCustom
							options={status_options}
							defaultValue={status.toLowerCase().replace(' ', '')}
							setDataFunction={(newStatus) =>
								handleStatusChange(newStatus, rowId)
							}
							label={
								status_options.find(
									(o) =>
										o.value ===
										status.toLowerCase().replace(' ', '')
								)?.label
							}
							renderValueAsLabel={true}
						/>
					)
				},
			},
			// {
			// 	field: 'menu',
			// 	headerName: '',
			// 	sortable: false,
			// 	filterable: false,
			// 	width: 64,
			// 	align: 'right',
			// 	renderCell: (params) => (
			// 		<Box
			// 			sx={{
			// 				display: 'flex',
			// 				alignItems: 'center',
			// 				justifyContent: 'flex-end',
			// 				width: '100%',
			// 				height: '100%',
			// 			}}
			// 		>
			// 			<IconButton
			// 				data-cy="kebab"
			// 				size="small"
			// 				onClick={(e) => openMenu(e, params.row)}
			// 				sx={{ p: 0.5 }}
			// 			>
			// 				<img src="/images/more.svg" alt="" />
			// 			</IconButton>
			// 		</Box>
			// 	),
			// },
		],
		[status_options, handleStatusChange]
	)

	const filteredRows = useMemo(() => {
		const q = search.trim().toLowerCase()
		return rows.filter((r) => {
			if (q && !r.task.toLowerCase().includes(q)) return false
			if (statusFilter.length && !statusFilter.includes(r.status))
				return false
			if (priorityFilter.length && !priorityFilter.includes(r.priority))
				return false
			return true
		})
	}, [rows, search, statusFilter, priorityFilter])

	const chips = [
		...statusFilter.map((s) => ({
			key: `status-${s}`,
			label: `Status ${s}`,
			onDelete: () =>
				setStatusFilter((prev) => prev.filter((x) => x !== s)),
		})),
		...priorityFilter.map((r) => ({
			key: `priority-${r}`,
			label: `Priority ${r}`,
			onDelete: () =>
				setPriorityFilter((prev) => prev.filter((x) => x !== r)),
		})),
	]

	const clearFilters = () => {
		setStatusFilter(availableStatuses)
		setPriorityFilter([])
	}

	const openFilter = (e) => setFilterAnchor(e.currentTarget)
	const closeFilter = () => setFilterAnchor(null)

	const exportCSV = () => {
		const header = [
			'Task',
			'Assigned To',
			'Due Date',
			'Priority',
			'Comments',
			'Status',
		]
		const rowsCSV = filteredRows.map((r) => [
			r.task,
			r.assignedTo,
			r.dueDate,
			r.priority,
			r.comments,
			r.status,
		])
		const csv = [header, ...rowsCSV]
			.map((r) => r.map(esc).join(','))
			.join('\n')
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = 'vendor-tasks.csv'
		a.click()
		URL.revokeObjectURL(url)
	}

	const openMenu = (e, row) => {
		e.stopPropagation()
		setMenuAnchor(e.currentTarget)
		setMenuRow(row)
	}
	const closeMenu = () => {
		setMenuAnchor(null)
		setMenuRow(null)
	}

	useEffect(() => {
		if (kazooId) dispatch(getKazooMembers(kazooId))
		dispatch(getKazooGuests(kazooId))
		dispatch(getEvents(kazooId))
		dispatch(fetchEventTags(kazooId))
	}, [dispatch, kazooId])

	const kazooMembers = useSelector((s) => s.kazooEvent.kazooMembers) || {}
	const role = useSelector((s) => s.kazooEvent.role)
	const kazooGuests = useSelector((s) => s.user.guests) || []

	const brideTier = kazooMembers?.bride?.payment
	const groomTier = kazooMembers?.groom?.payment

	const coupleHasPremium = useCoupleHasPremium(brideTier, groomTier)
	const hasPremiumAccess = coupleHasPremium || ['vendor', 'vendor-staff', 'planner-staff'].includes(role)

	const clearTaskModal = () => {
		setTaskTitle('')
		setSelectedMembers([])
		setDueDate(null)
		setPriorityVal('high')
		setStatusVal('new')
		setEventCategory({ label: '', value: '' })
		setEventTags([])
		setText('')
		setSyncToCalendar({ google: true, outlook: true, ical: true })
	}

	const weddingPeople = useMemo(() => {
		const {
			bride = {},
			groom = {},
			members = [],
			vendors = [],
			planners = [],
		} = kazooMembers

		const raw = []
			.concat(bride?._id ? [bride] : [])
			.concat(groom?._id ? [groom] : [])
			.concat(Array.isArray(members) ? members : [])
			.concat(Array.isArray(vendors) ? vendors : [])
			.concat(Array.isArray(kazooGuests) ? kazooGuests : [])
			.concat(Array.isArray(planners) ? planners : [])
		const seen = new Set()
		const unique = []
		for (const p of raw) {
			const id = p?._id || p?.memberId || p?.id
			if (!id || seen.has(String(id))) continue
			seen.add(String(id))
			unique.push(p)
		}

		const toName = (u) =>
			u.businessName ||
			u.preferredName ||
			[u.firstName, u.lastName].filter(Boolean).join(' ') ||
			u.email ||
			'Unknown User'

		return unique.map((u) => ({
			...u,
			_id: String(u._id || u.memberId || u.id),
			name: toName(u),
			avatar: u.profilePic || u.avatar || undefined,
		}))
	}, [kazooMembers, kazooGuests])

	const selectablePeople = useMemo(() => {
		const selectedIds = new Set(selectedMembers.map((m) => m._id))
		return weddingPeople.filter((p) => !selectedIds.has(p._id))
	}, [weddingPeople, selectedMembers])

	const handleSelect = (member) => {
		setSelectedMembers((prev) => [...prev, member])
	}

	const handleRemove = (id) => {
		setSelectedMembers((prev) => prev.filter((m) => m._id !== id))
	}

	const handleSubmit = async (e) => {
		e.preventDefault()

		if (!taskTitle.trim()) {
			toastHelper('Task title is required', 'error')
			return
		}
		if (!selectedMembers.length) {
			toastHelper('Please select at least one assignee', 'error')
			return
		}
		if (!eventCategory.value.length) {
			toastHelper('Please select an event category', 'error')
			return
		}
		if (!dueDate) {
			toastHelper('Please choose a due date', 'error')
			return
		}

		const due =
			dueDate instanceof Date
				? dueDate.toISOString()
				: new Date(dueDate).toString() !== 'Invalid Date'
					? new Date(dueDate).toISOString()
					: undefined

		if (!due) {
			toastHelper('Invalid due date', 'error')
			return
		}

		const priorityKey = getVal(priorityVal) // 'high' | 'medium' | 'low'
		const statusKey = getVal(statusVal) // 'new' | 'inprogress' | 'completed' | 'overdue'

		const data = {
			eventCategory: eventCategory?.value || '',
			title: taskTitle.trim(),
			selectedMembers,
			StartTime: due,
			EndTime: due,
			priority: PRIORITY_LABEL[priorityKey] ?? 0,
			status: STATUS_LABEL[statusKey] || 'New',
			comments: text,
			eventTags,
			syncToCalendar,
		}

		dispatch(createTask({ kazooId, data }))
		clearTaskModal()
		closeAllModals()
	}

	const openCreateModal = () => setActiveModal('create')
	const openDeleteModal = () => setActiveModal('delete')
	const openDeleteModalFor = (id) => {
		setCurrentTaskId(id || null)
		setActiveModal('delete')
	}
	const closeAllModals = () => {
		setActiveModal('')
		setCurrentTaskId(null)
	}

	const handleRowClick = ({ row }) => {
		setCurrentTaskId(row.id)
		setActiveModal('detail')
	}

	const onConfirmDelete = async () => {
		if (!currentTaskId) {
			toastHelper('No task selected', 'error')
			return
		}

		dispatch(deleteTask({ kazooId, id: currentTaskId }))
		closeAllModals()
	}

	const onConfirmEdit = async () => {
		if (!currentTaskId) {
			toastHelper('No task selected', 'error')
			return
		}

		if (!taskTitle.trim()) {
			toastHelper('Task title is required', 'error')
			return
		}
		if (!selectedMembers.length) {
			toastHelper('Please select at least one assignee', 'error')
			return
		}
		if (!eventCategory.value.length) {
			toastHelper('Please select an event category', 'error')
			return
		}
		if (!dueDate) {
			toastHelper('Please choose a due date', 'error')
			return
		}

		const due =
			dueDate instanceof Date
				? dueDate.toISOString()
				: new Date(dueDate).toString() !== 'Invalid Date'
					? new Date(dueDate).toISOString()
					: undefined

		if (!due) {
			toastHelper('Invalid due date', 'error')
			return
		}

		const priorityKey = getVal(priorityVal) // 'high' | 'medium' | 'low'
		const statusKey = getVal(statusVal) // 'new' | 'inprogress' | 'completed' | 'overdue'

		const payload = {
			title: taskTitle.trim(),
			selectedMembers,
			StartTime: due,
			EndTime: due,
			priority: PRIORITY_LABEL[priorityKey] ?? 0,
			status: STATUS_LABEL[statusKey] || 'New',
			comments: text,
			eventCategory: eventCategory?.value,
			eventTags,
			syncToCalendar,
		}

		dispatch(updateTask({ kazooId, id: currentTaskId, data: payload }))
		clearTaskModal()
		closeAllModals()
	}

	// Fast lookup for people by id
	const peopleById = useMemo(() => {
		const m = new Map()
		for (const p of weddingPeople) m.set(String(p._id), p)
		return m
	}, [weddingPeople])

	useEffect(() => {
		const t =
			tasksOnly.find((t) => String(t._id) === String(currentTaskId)) ||
			null
		setCurrentTask(t)
	}, [currentTaskId, tasksOnly])

	const taskAssignees = useMemo(() => {
		const ids = Array.isArray(currentTask?.OwnerIds)
			? currentTask.OwnerIds
			: []
		return ids
			.map((id) => {
				const p = peopleById.get(String(id))
				return (
					p && {
						id: String(id),
						name: p.name || 'Unknown User',
						avatar:
							p.avatar ||
							'https://static.vecteezy.com/system/resources/previews/020/911/740/non_2x/user-profile-icon-profile-avatar-user-icon-male-icon-face-icon-profile-icon-free-png.png',
					}
				)
			})
			.filter(Boolean)
	}, [currentTask, peopleById])

	const dueDisplay = useMemo(() => {
		const d = currentTask?.EndTime || currentTask?.StartTime
		if (!d) return '-'
		return formatUtcToLocalDate(d)
	}, [currentTask])

	const prioLabel = useMemo(() => {
		const map = { 0: 'High', 1: 'Medium', 2: 'Low' }
		return map[Number(currentTask?.priority)] || 'Unknown'
	}, [currentTask])

	const prioIcon = useMemo(() => {
		return prioLabel === 'High'
			? '/images/high-prio-icon.svg'
			: prioLabel === 'Medium'
				? '/images/med-prio-icon.svg'
				: '/images/low-prio-icon.svg'
	}, [prioLabel])

	const statusPretty = useMemo(() => {
		const s = String(currentTask?.status || '').trim()
		if (!s) return 'New'
		return s.toLowerCase() === 'in progress' ? 'In Progress' : s
	}, [currentTask])

	const statusVariant = useMemo(() => {
		const s = statusPretty.toLowerCase()
		return s === 'completed'
			? 'submitted'
			: s === 'in progress'
				? 'pending'
				: s === 'overdue'
					? 'closed'
					: 'active'
	}, [statusPretty])

	const openEditFromTask = (taskArg) => {
		const task = taskArg || currentTask
		if (!task) return

		setTaskTitle(task.Subject || '')
		setText(task.Description || '')

		// assignees
		const members = (Array.isArray(task.OwnerIds) ? task.OwnerIds : [])
			.map((id) => peopleById.get(String(id)))
			.filter(Boolean)
		setSelectedMembers(members)

		// dates
		const d = task.EndTime || task.StartTime || null
		setDueDate(d ? formatUtcToLocalDate(d) : null)

		// priority
		const prMap = { 0: 'high', 1: 'medium', 2: 'low' }
		setPriorityVal(prMap[Number(task.priority)] || 'high')

		// status
		const s = String(task.status || 'New').toLowerCase()
		const sKey =
			s === 'in progress'
				? 'inprogress'
				: s === 'completed'
					? 'completed'
					: s === 'overdue'
						? 'overdue'
						: 'new'
		setStatusVal(sKey)

		const cat = task.EventCategory || ''
		setEventCategory({ value: cat, label: cat })
		setEventTags(Array.isArray(task.EventTags) ? task.EventTags : [])

		const sync = task.syncToCalendar
		setSyncToCalendar({
			google: sync?.google !== false,
			outlook: sync?.outlook !== false,
			ical: sync?.ical !== false,
		})

		setActiveModal('edit')
	}

	const handlePriorityChange = (v) => setPriorityVal(normVal(v))

	return (
		<div className={styles.pageWrap}>
			<div className={styles.pageHeader}>
				<Typography component="h1">Tasks</Typography>
				<div className={styles.right}>
					<ButtonCustom
						variant="default"
						size="lg"
						onClick={() => {
							if (!hasPremiumAccess) {
								setShowUpgradePopup(true)
								return
							}
							clearTaskModal()
							openCreateModal()
						}}
						data-cy="add-task"
					>
						+ Add Task
					</ButtonCustom>
					<SyncAllModal />
				</div>
			</div>

			<Box className={styles.tableWrap}>
				<ListToolbar
					title="Task"
					searchPlaceholder="Search task"
					searchValue={search}
					onSearchChange={setSearch}
					chips={chips}
					onClearFilters={clearFilters}
					onOpenFilter={openFilter}
					onExport={exportCSV}
				/>
				<FilterPopover
					anchorEl={filterAnchor}
					open={Boolean(filterAnchor)}
					onClose={closeFilter}
					onClear={clearFilters}
					onApply={closeFilter}
					sections={[
						{
							id: 'status',
							title: 'Status',
							options: [
								'New',
								'In Progress',
								'Completed',
								'Overdue',
							],
							selected: statusFilter,
							onToggle: (val, checked) =>
								setStatusFilter((prev) =>
									checked
										? [...prev, val]
										: prev.filter((x) => x !== val)
								),
						},
						{
							id: 'priority',
							title: 'Priority',
							options: ['High', 'Medium', 'Low'],
							selected: priorityFilter,
							onToggle: (val, checked) =>
								setPriorityFilter((prev) =>
									checked
										? [...prev, val]
										: prev.filter((x) => x !== val)
								),
						},
					]}
				/>

				<DataGridTable
					rows={filteredRows}
					columns={columns}
					onRowClick={handleRowClick}
				/>

				<KebabMenu
					anchorEl={menuAnchor}
					open={Boolean(menuAnchor)}
					onClose={closeMenu}
					items={[
						{
							key: 'edit',
							label: 'Edit',
							icon: (
								<>
									<img src="/images/edit-icon.svg" alt="" />
								</>
							),
							onClick: () => {
								const t = tasksOnly.find(
									(x) => String(x._id) === String(menuRow?.id)
								)
								setCurrentTaskId(menuRow?.id)
								closeMenu()
								openEditFromTask(t)
							},
						},
						{
							key: 'delete',
							label: 'Delete',
							icon: (
								<>
									<img src="/images/trash-red.svg" alt="" />
								</>
							),
							onClick: () => openDeleteModalFor(menuRow?.id),
							color: 'error.main',
						},
					]}
				/>
			</Box>

			{/* Create Modal */}
			<CommonModal
				open={activeModal === 'create'}
				handleClose={() => {
					clearTaskModal()
					closeAllModals()
				}}
				heading="Add Task"
				footerContent={
					<>
						<ButtonCustom
							onClick={() => {
								clearTaskModal()
								closeAllModals()
							}}
							// disabled={loading}
							variant="outlined"
							size="lg"
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="default"
							size="lg"
							onClick={handleSubmit}
						>
							+ Add Task
						</ButtonCustom>
					</>
				}
			>
				<form onSubmit={handleSubmit} className={styles.formWrp}>
					<Grid container spacing={1.5}>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label htmlFor="task" className={styles.label}>
									Task Title
								</label>
								<input
									type="text"
									name="task"
									id="task"
									placeholder="Enter task title"
									className={styles.taskTitleInput}
									value={taskTitle}
									onChange={(e) => setTaskTitle(e.target.value)}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label htmlFor="name" className={styles.label}>
									Assigned to
								</label>
								<MultiselectSearch
									members={selectablePeople}
									selected={selectedMembers}
									onSelect={handleSelect}
									onRemove={handleRemove}
									// label="Assigned to"
									placeholder="Select user"
									searchPlaceholder="Search user..."
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label
									htmlFor="EventCategory"
									className={styles.label}
								>
									Event Category
								</label>
								<SelectCustom
									label="Event category"
									options={eventCategoriesOptions}
									defaultValue={eventCategory.value}
									setDataFunction={setEventCategory}
									renderValueAsLabel={true}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<EventTagsField
									value={eventTags}
									onChange={setEventTags}
									catalog={eventTagCatalog}
									kazooId={kazooId}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label
									htmlFor="DueDate"
									className={styles.label}
								>
									Due Date
								</label>
								<DateCustom
									value={dueDate}
									onChange={setDueDate}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, sm: 6 }}>
							<div className={styles.formField}>
								<label
									htmlFor="Priority"
									className={styles.label}
								>
									Priority
								</label>
								<SelectCustom
									options={priority_options}
									defaultValue={priorityVal}
									setDataFunction={handlePriorityChange}
									label={
										priority_options.find(
											(o) => o.value === priorityVal
										)?.label
									}
									renderValueAsLabel={true}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, sm: 6 }}>
							<div className={styles.formField}>
								<label
									htmlFor="Status"
									className={styles.label}
								>
									Status
								</label>
								<SelectCustom
									options={status_options}
									defaultValue={statusVal}
									setDataFunction={handleStatusChange}
									label={
										status_options.find(
											(o) => o.value === statusVal
										)?.label
									}
									renderValueAsLabel={true}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label
									htmlFor="Comments"
									className={styles.label}
								>
									Comments
								</label>
								<TextArea
									placeholder="Add comments"
									value={text}
									onChange={(e) => setText(e.target.value)}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<CalendarSync
									value={syncToCalendar}
									onChange={setSyncToCalendar}
									labelClassName={styles.label}
								/>
							</div>
						</Grid>
						{/* <Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label
									htmlFor="Attachment"
									className={styles.label}
								>
									Attachment
								</label>
								<FileAttach
									label="Choose File"
									placeholder="No Files Selected"
									accept=".jpg,.png,.pdf"
									multiple
									onChange={(e) =>
										console.log(e.target.files)
									}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, sm: 6 }}>
							<div className={styles.formField}>
								<ButtonCustom
									variant="outlined"
									size="lg"
									className={styles.fileUploadedList}
								>
									<img src="/images/document.svg" />
									<Typography variant="body">
										Task Details.pdf
									</Typography>
									<img src="/images/file-close-icon-black.svg" />
								</ButtonCustom>
							</div>
						</Grid>
						<Grid size={{ xs: 12, sm: 6 }}>
							<div className={styles.formField}>
								<ButtonCustom
									variant="outlined"
									size="lg"
									className={styles.fileUploadedList}
								>
									<img src="/images/document.svg" />
									<Typography variant="body">
										Task Details.pdf
									</Typography>
									<img src="/images/file-close-icon-black.svg" />
								</ButtonCustom>
							</div>
						</Grid> */}
					</Grid>
				</form>
			</CommonModal>

			{/* Edit Modal */}
			<CommonModal
				open={activeModal === 'edit'}
				handleClose={() => {
					clearTaskModal()
					closeAllModals()
				}}
				heading="Edit Task"
				className={styles.taskEditModal}
				footerContent={
					<>
						<ButtonCustom
							onClick={() => {
								clearTaskModal()
								closeAllModals()
							}}
							// disabled={loading}
							variant="outlined"
							size="lg"
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="default"
							size="lg"
							onClick={onConfirmEdit}
						// disabled={!canSubmit}
						>
							Update
						</ButtonCustom>
					</>
				}
			>
				<>
					<Typography
						variant="body1"
						className={styles.subHeading}
					></Typography>
					<form onSubmit={handleSubmit} className={styles.formWrp}>
						<Grid container spacing={1.5}>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label htmlFor="task" className={styles.label}>
										Task Title
									</label>
									<input
										type="text"
										name="task"
										id="task"
										placeholder="Enter task title"
										className={styles.taskTitleInput}
										value={taskTitle}
										onChange={(e) =>
											setTaskTitle(e.target.value)
										}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="name"
										className={styles.label}
									>
										Assigned to
									</label>
									<MultiselectSearch
										members={selectablePeople}
										selected={selectedMembers}
										onSelect={handleSelect}
										onRemove={handleRemove}
										// label="Assigned to"
										placeholder="Select user"
										searchPlaceholder="Search user..."
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="EventCategory"
										className={styles.label}
									>
										Event Category
									</label>
									<SelectCustom
										label="Event category"
										options={eventCategoriesOptions}
										defaultValue={eventCategory.value}
										setDataFunction={setEventCategory}
										renderValueAsLabel={true}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<EventTagsField
										value={eventTags}
										onChange={setEventTags}
										catalog={eventTagCatalog}
										kazooId={kazooId}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="DueDate"
										className={styles.label}
									>
										Due Date
									</label>
									<DateCustom
										value={dueDate}
										onChange={setDueDate}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<div className={styles.formField}>
									<label
										htmlFor="Priority"
										className={styles.label}
									>
										Priority
									</label>
									<SelectCustom
										options={priority_options}
										defaultValue={priorityVal}
										setDataFunction={handlePriorityChange}
										renderValueAsLabel={true}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<div className={styles.formField}>
									<label
										htmlFor="Status"
										className={styles.label}
									>
										Status
									</label>
									<SelectCustom
										options={status_options}
										defaultValue={statusVal}
										setDataFunction={handleStatusChange}
										label={
											status_options.find(
												(o) => o.value === statusVal
											)?.label
										}
										renderValueAsLabel={true}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="Comments"
										className={styles.label}
									>
										Comments
									</label>
									<TextArea
										placeholder="Add comments"
										value={text}
										onChange={(e) =>
											setText(e.target.value)
										}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<CalendarSync
										value={syncToCalendar}
										onChange={setSyncToCalendar}
										labelClassName={styles.label}
									/>
								</div>
							</Grid>
							{/* <Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="Attachment"
										className={styles.label}
									>
										Attachment
									</label>
									<FileAttach
										label="Choose File"
										placeholder="No Files Selected"
										accept=".jpg,.png,.pdf"
										multiple
										onChange={(e) =>
											console.log(e.target.files)
										}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<div className={styles.formField}>
									<ButtonCustom
										variant="outlined"
										size="lg"
										className={styles.fileUploadedList}
									>
										<img src="/images/document.svg" />
										<Typography variant="body">
											Task Details.pdf
										</Typography>
										<img src="/images/file-close-icon-black.svg" />
									</ButtonCustom>
								</div>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<div className={styles.formField}>
									<ButtonCustom
										variant="outlined"
										size="lg"
										className={styles.fileUploadedList}
									>
										<img src="/images/document.svg" />
										<Typography variant="body">
											Task Details.pdf
										</Typography>
										<img src="/images/file-close-icon-black.svg" />
									</ButtonCustom>
								</div>
							</Grid> */}
						</Grid>
					</form>
				</>
			</CommonModal>

			{/* Detail Modal */}
			<CommonModal
				open={activeModal === 'detail'}
				handleClose={closeAllModals}
				heading="Task Detail"
				className={styles.taskDetailsModal}
				footerContent={
					<>
						<ButtonCustom
							onClick={() => {
								if (currentTask?._id)
									setCurrentTaskId(currentTask._id)
								openDeleteModal()
							}}
							variant="outlined"
							size="lg"
						>
							Delete
						</ButtonCustom>
						<ButtonCustom
							variant="default"
							size="lg"
							onClick={() => openEditFromTask()}
						>
							Edit
						</ButtonCustom>
					</>
				}
			>
				<>
					<Typography variant="body1" className={styles.subHeading}>
						{currentTask?.Subject || 'Untitled Task'}
					</Typography>
					<form onSubmit={handleSubmit} className={styles.formWrp}>
						<Grid container spacing={1.7}>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="name"
										className={styles.label}
									>
										Assigned to
									</label>
									<ul className={styles.assignedToRow}>
										{taskAssignees.length > 0 ? (
											taskAssignees.map((m) => (
												<li key={m.id} title={m.name}>
													<img
														src={m.avatar}
														alt={m.name}
													/>
												</li>
											))
										) : (
											<li className={styles.empty}>
												Unassigned
											</li>
										)}
									</ul>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label className={styles.label}>
										Event Category
									</label>
									<div className={styles.dueDate}>
										{currentTask?.EventCategory || '-'}
									</div>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="DueDate"
										className={styles.label}
									>
										Due Date
									</label>
									<div className={styles.dueDate}>
										{dueDisplay}
									</div>
								</div>
							</Grid>
							{/* Priority */}
							<Grid size={{ xs: 12, sm: 12 }}>
								<div className={styles.formField}>
									<label className={styles.label}>
										Priority
									</label>
									<div
										className={`${styles.prioritySelect} prioritySelect`}
									>
										<img src={prioIcon} alt={prioLabel} />
										{prioLabel}
									</div>
								</div>
							</Grid>
							{/* Status */}
							<Grid size={{ xs: 12, sm: 12 }}>
								<div className={styles.formField}>
									<label className={styles.label}>
										Status
									</label>
									<StatusBtn
										label={statusPretty}
										variant={statusVariant}
										size="small"
										className={styles.status}
									/>
								</div>
							</Grid>

							{/* Comments */}
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label className={styles.label}>
										Comments
									</label>
									<div className={styles.comment}>
										{currentTask?.Description || '-'}
									</div>
								</div>
							</Grid>

							{/* <Grid size={{ xs: 12, sm: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="Attachment"
										className={styles.label}
									>
										Attachment
									</label>
									<Grid container spacing={1.5}>
										<Grid size={{ xs: 12, sm: 6 }}>
											<ButtonCustom
												variant="outlined"
												size="lg"
												className={
													styles.fileUploadedList
												}
											>
												<img src="/images/document.svg" />
												<Typography variant="body">
													Task Details.pdf
												</Typography>
												<img src="/images/task-detail-download.svg" />
											</ButtonCustom>
										</Grid>
										<Grid size={{ xs: 12, sm: 6 }}>
											<ButtonCustom
												variant="outlined"
												size="lg"
												className={
													styles.fileUploadedList
												}
											>
												<img src="/images/document.svg" />
												<Typography variant="body">
													Task Details.pdf
												</Typography>
												<img src="/images/task-detail-download.svg" />
											</ButtonCustom>
										</Grid>
									</Grid>
								</div>
							</Grid> */}
						</Grid>
					</form>
				</>
			</CommonModal>

			{/* Delete modal */}
			<CommonDeleteModal
				open={activeModal === 'delete'}
				handleClose={closeAllModals}
				heading="Delete Task?"
				subHeading="Are you sure you want to delete this task?"
				modalBody={styles.modalBody}
				className={styles.deleteModal}
			>
				<form className={styles.formBox}>
					<div
						className={styles.modalBtns}
						style={{ justifyContent: 'center', marginTop: '0' }}
					>
						<ButtonCustom
							variant="outlined"
							type="button"
							size="lg"
							onClick={closeAllModals}
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="destructive"
							size="lg"
							onClick={onConfirmDelete}
						>
							Delete
						</ButtonCustom>
					</div>
				</form>
			</CommonDeleteModal>
			{showUpgradePopup && (
				<GeneralPaymentPopup
					clickHandler={() => setShowUpgradePopup(false)}
				/>
			)}
		</div>
	)
}
